import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  notification,
} from "antd";
import { useContext, useEffect, useState } from "react";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  TranslationOutlined,
} from "@ant-design/icons";

import "./styles.css";

import { Context } from "../../context";
import { Fetch, Post, Put, Delete } from "../../fetch";
import BrowseLiveOffers from "./BrowseLiveOffers";
import LocalizeOffer from "./LocalizeOffer";

const { Column } = Table;
const { Option } = Select;
const { TabPane } = Tabs;
const { confirm } = Modal;

const CATEGORIES = [
  { value: "non_gaming", label: "Non Gaming", color: "blue" },
  { value: "gaming", label: "Gaming", color: "purple" },
  { value: "survey", label: "Survey", color: "green" },
];

const categoryLabel = (value) =>
  CATEGORIES.find((c) => c.value === value)?.label || value;

const categoryColor = (value) =>
  CATEGORIES.find((c) => c.value === value)?.color || "default";

const PubScaleAds = () => {
  const { token, apiUrl } = useContext(Context);

  const [offers, setOffers] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modalType, setModalType] = useState("Add");
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [counter, setCounter] = useState(0);
  const [browseVisible, setBrowseVisible] = useState(false);
  const [localizeOffer, setLocalizeOffer] = useState(null);

  const [form] = Form.useForm();

  useEffect(() => {
    Fetch(`${apiUrl}/v4/tasks-config/offers`, token, (result) => {
      if (result && result.success !== false) {
        setOffers(result.offers || []);
      }
    });
  }, [apiUrl, token, counter]);

  const visibleOffers = (
    activeCategory === "all"
      ? offers
      : offers.filter((offer) => offer.category === activeCategory)
  )
    .slice()
    .sort(
      (a, b) =>
        (a.category || "").localeCompare(b.category || "") ||
        (a.display_order || 0) - (b.display_order || 0)
    );

  // Offer ids already curated — the browse modal disables/marks these.
  const existingOfferIds = offers.map((o) => o.offer_id);

  // Next display_order to append within a category when adding new offers.
  const nextOrderForCategory = (category) => {
    const inCat = offers.filter((o) => o.category === category);
    if (inCat.length === 0) return 0;
    return Math.max(...inCat.map((o) => o.display_order || 0)) + 1;
  };

  /**
   * Move an offer up/down within its category and persist the new order.
   * Reindexes the whole category to sequential display_order, applies the
   * swap, optimistically updates local state, then PUTs each changed row.
   */
  const moveOffer = (record, direction) => {
    const inCat = offers
      .filter((o) => o.category === record.category)
      .slice()
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    const idx = inCat.findIndex((o) => o.id === record.id);
    const target = idx + (direction === "up" ? -1 : 1);
    if (idx < 0 || target < 0 || target >= inCat.length) return;

    const reordered = inCat.slice();
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];

    // New sequential display_order = array index.
    const changed = [];
    reordered.forEach((offer, i) => {
      if ((offer.display_order || 0) !== i) changed.push({ ...offer, display_order: i });
    });
    if (changed.length === 0) return;

    // Optimistic local update.
    setOffers((prev) =>
      prev.map((o) => {
        const c = changed.find((x) => x.id === o.id);
        return c ? { ...o, display_order: c.display_order } : o;
      })
    );

    // Persist each changed row; refresh from server on any failure.
    changed.forEach((offer) => {
      Put(
        `${apiUrl}/v4/tasks-config/offers/${offer.id}`,
        token,
        { display_order: offer.display_order },
        (result) => {
          if (!result || !result.success) setCounter((c) => c + 1);
        },
        () => setCounter((c) => c + 1)
      );
    });
  };

  const showAddModal = () => {
    setModalType("Add");
    setEditingOfferId(null);
    form.resetFields();
    setModalVisible(true);
  };

  const showEditModal = (record) => {
    setModalType("Edit");
    setEditingOfferId(record.id);
    form.setFieldsValue({
      offer_id: record.offer_id,
      name: record.name,
      category: record.category,
      enabled: record.enabled ?? false,
      display_order: record.display_order ?? 0,
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingOfferId(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    let fields;
    try {
      fields = await form.validateFields();
    } catch (err) {
      notification.error({
        message: `${modalType} Offer Error`,
        description: "Required fields are incorrect/missing",
      });
      return;
    }

    const payload = {
      offer_id: fields.offer_id,
      name: fields.name,
      category: fields.category,
      enabled: fields.enabled ?? false,
      display_order: fields.display_order ?? 0,
    };

    setConfirmLoading(true);

    if (modalType === "Edit") {
      Put(
        `${apiUrl}/v4/tasks-config/offers/${editingOfferId}`,
        token,
        payload,
        (result) => {
          setConfirmLoading(false);
          if (result.success) {
            notification.success({
              message: "Success",
              description: "Offer updated",
            });
            closeModal();
            setCounter((c) => c + 1);
          } else {
            notification.error({
              message: "Error updating offer",
              description: result.message || "Check console for more details",
            });
          }
        },
        () => {
          setConfirmLoading(false);
          notification.error({
            message: "Error updating offer",
            description: "Check console for more details",
          });
        }
      );
      return;
    }

    Post(
      `${apiUrl}/v4/tasks-config/offers`,
      token,
      payload,
      () => {
        setConfirmLoading(false);
        notification.success({
          message: "Success",
          description: "Offer created",
        });
        closeModal();
        setCounter((c) => c + 1);
      },
      (errStatus) => {
        setConfirmLoading(false);
        if (errStatus === "409") {
          notification.error({
            message: "Duplicate offer",
            description: `An offer with offer_id ${payload.offer_id} already exists`,
          });
        } else {
          notification.error({
            message: "Error creating offer",
            description: "Check console for more details",
          });
        }
      }
    );
  };

  const handleEnabledToggle = (record, checked) => {
    // Optimistic update so the switch feels immediate.
    setOffers((prev) =>
      prev.map((offer) =>
        offer.id === record.id ? { ...offer, enabled: checked } : offer
      )
    );
    Put(
      `${apiUrl}/v4/tasks-config/offers/${record.id}`,
      token,
      { enabled: checked },
      (result) => {
        if (!result.success) {
          notification.error({
            message: "Error updating offer",
            description: result.message || "Check console for more details",
          });
          setCounter((c) => c + 1);
        }
      },
      () => {
        notification.error({
          message: "Error updating offer",
          description: "Check console for more details",
        });
        setCounter((c) => c + 1);
      }
    );
  };

  const confirmDelete = (record) => {
    confirm({
      title: `Are you sure you want to delete offer "${record.name}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        Delete(
          `${apiUrl}/v4/tasks-config/offers/${record.id}`,
          token,
          (result) => {
            if (result.success) {
              notification.success({
                message: "Success",
                description: "Offer deleted successfully",
              });
              setCounter((c) => c + 1);
            } else {
              notification.error({
                message: "Error deleting offer",
                description:
                  result.message || "Check console for more details",
              });
            }
          },
          () => {
            notification.error({
              message: "Error deleting offer",
              description: "Check console for more details",
            });
          }
        );
      },
    });
  };

  return (
    <div className="pubscale-ads-wrapper">
      <div className="pubscale-ads-toolbar">
        <Tabs activeKey={activeCategory} onChange={setActiveCategory}>
          <TabPane tab="All" key="all" />
          {CATEGORIES.map((category) => (
            <TabPane tab={category.label} key={category.value} />
          ))}
        </Tabs>
        <Space>
          <Button icon={<SearchOutlined />} onClick={() => setBrowseVisible(true)}>
            Browse Live Offers
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
            Add Offer
          </Button>
        </Space>
      </div>
      <Table dataSource={visibleOffers} rowKey="id">
        <Column
          title="Offer ID"
          dataIndex="offer_id"
          key="offer_id"
          align="center"
        />
        <Column title="Name" dataIndex="name" key="name" align="center" />
        <Column
          title="Category"
          dataIndex="category"
          key="category"
          align="center"
          render={(category) => (
            <Tag color={categoryColor(category)}>{categoryLabel(category)}</Tag>
          )}
        />
        <Column
          title="Enabled"
          dataIndex="enabled"
          key="enabled"
          align="center"
          render={(enabled, record) => (
            <Switch
              checked={Boolean(enabled)}
              onChange={(checked) => handleEnabledToggle(record, checked)}
            />
          )}
        />
        <Column
          title="Order"
          key="order"
          align="center"
          render={(text, record) => (
            <Space size={4}>
              <Button
                size="small"
                type="text"
                icon={<ArrowUpOutlined />}
                onClick={() => moveOffer(record, "up")}
                title="Move up within category"
              />
              <span>{record.display_order ?? 0}</span>
              <Button
                size="small"
                type="text"
                icon={<ArrowDownOutlined />}
                onClick={() => moveOffer(record, "down")}
                title="Move down within category"
              />
            </Space>
          )}
        />
        <Column
          title="Actions"
          key="actions"
          align="center"
          render={(text, record) => (
            <Space size="middle">
              <Typography.Link onClick={() => showEditModal(record)}>
                Edit
              </Typography.Link>
              <Typography.Link onClick={() => setLocalizeOffer(record)}>
                <TranslationOutlined /> Localize
              </Typography.Link>
              <Typography.Link onClick={() => confirmDelete(record)}>
                Delete
              </Typography.Link>
            </Space>
          )}
        />
      </Table>
      <Modal
        title={`${modalType} Offer`}
        visible={modalVisible}
        onOk={handleSubmit}
        okText="Submit"
        onCancel={closeModal}
        confirmLoading={confirmLoading}
        destroyOnClose={true}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ enabled: true, display_order: 0 }}
        >
          <Form.Item
            name="offer_id"
            label="Offer ID"
            rules={[
              { required: true, message: "Offer ID is required" },
              {
                type: "integer",
                min: 0,
                message: "Offer ID must be an integer",
              },
            ]}
          >
            <InputNumber
              min={0}
              precision={0}
              placeholder="Enter PubScale offer id"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="Enter offer name" />
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: "Category is required" }]}
          >
            <Select placeholder="Choose a category">
              {CATEGORIES.map((category) => (
                <Option key={category.value} value={category.value}>
                  {category.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="enabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="display_order"
            label="Display Order"
            rules={[
              {
                type: "integer",
                min: 0,
                message: "Display order must be an integer >= 0",
              },
            ]}
          >
            <InputNumber min={0} precision={0} placeholder="Enter order" />
          </Form.Item>
        </Form>
      </Modal>
      <BrowseLiveOffers
        visible={browseVisible}
        onClose={() => setBrowseVisible(false)}
        apiUrl={apiUrl}
        token={token}
        existingOfferIds={existingOfferIds}
        nextOrderForCategory={nextOrderForCategory}
        onAdded={() => setCounter((c) => c + 1)}
      />
      <LocalizeOffer
        visible={!!localizeOffer}
        onClose={() => setLocalizeOffer(null)}
        apiUrl={apiUrl}
        token={token}
        offer={localizeOffer}
        onSaved={() => setCounter((c) => c + 1)}
      />
    </div>
  );
};

export default PubScaleAds;
